import { Box, withStyles, getCSSVariable } from 'vopay';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO, subDays, subMonths } from 'date-fns';
import { useDataFetchIndicator } from '@/hooks';

import TransactionChart from '../../components/TransactionChart';
import TabButton from '../../components/TabButton';
import useTransactionCount from './useTransactionCount';
import styles from './TransactionsCard.module.scss';

import { Title } from 'UI';

const cn = withStyles(styles);

type Tab = 'weekly' | 'monthly';

type TransactionsCardProps = {
  currency: 'CAD' | 'USD';
};

type FC = FunctionComponent<TransactionsCardProps>;

const TransactionsCard: FC = ({ currency }) => {
  const { t } = useTranslation('translations', { keyPrefix: 'HomepageRevamp' });
  const { fetchTransactionCount } = useTransactionCount();
  const [, setDataFetchingInfo] = useDataFetchIndicator();

  const [data, setData] = useState({
    weekly: { transactions: {}, count: 0 },
    monthly: { transactions: {}, count: 0 },
  });

  const [activeTab, setActiveTab] = useState<Tab>('weekly');
  const primaryColor = getCSSVariable('--primaryColor');
  const primaryHexWithOpacityBorder = `${primaryColor}80`;
  const primaryHexWithOpacityBackground = `${primaryColor}1A`;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const now = new Date();
        const endDate = format(now, 'yyyy-MM-dd');
        setDataFetchingInfo(true);
        const [weeklyRes, monthlyRes] = await Promise.all([
          fetchTransactionCount({
            Currency: currency,
            StartDateTime: format(subDays(now, 6), 'yyyy-MM-dd'),
            EndDateTime: endDate,
          }),
          fetchTransactionCount({
            Currency: currency,
            StartDateTime: format(subMonths(now, 1), 'yyyy-MM-dd'),
            EndDateTime: endDate,
          }),
        ]);
        setData({
          weekly: {
            transactions: weeklyRes.CountData,
            count: Object.values(weeklyRes.CountData).reduce((sum, val) => sum + val, 0),
          },
          monthly: {
            transactions: monthlyRes.CountData,
            count: Object.values(monthlyRes.CountData).reduce((sum, val) => sum + val, 0),
          },
        });
      } catch (err) {
        console.error(err);
      } finally {
        setDataFetchingInfo(false);
      }
    };
    fetchData();
  }, [currency]);

  const options: { label: string; value: Tab }[] = [
    { label: t('Widgets.Transactions.weekly'), value: 'weekly' },
    { label: t('Widgets.Transactions.monthly'), value: 'monthly' },
  ];

  return (
    <>
      <Box ai="center" jc="sb" mb="md" height="auto">
        <Title level="5">{t('Widgets.Transactions.title')}</Title>
        <TabButton
          options={options}
          value={activeTab}
          onSelect={(val: Tab) => {
            setActiveTab(val);
          }}
        />
      </Box>
      <TransactionChart
        data={Object.entries(activeTab === 'weekly' ? data.weekly.transactions : data.monthly.transactions).map(
          ([date, count]) => ({
            label: format(parseISO(date), 'MMM d'),
            value: count as number,
          }),
        )}
        options={{
          label: t('Widgets.Transactions.label'),
          fill: true,
          tension: 0.2,
          ...(activeTab === 'monthly' && { tickSpace: 7 }),
        }}
      />
      <div className={cn('transactionBottomCardContainer')}>
        {options.map((opt) => {
          const isActive = activeTab === opt.value;

          return (
            <button
              key={opt.value}
              className={cn('transactionBottomCard')}
              {...(isActive && {
                style: {
                  border: `2px solid ${primaryHexWithOpacityBorder}`,
                  backgroundColor: primaryHexWithOpacityBackground,
                },
              })}
              onClick={() => {
                setActiveTab(opt.value);
              }}
            >
              {opt.label}
              <h4 style={{ fontWeight: isActive ? 600 : 400 }}>
                {opt.value === 'weekly' ? data.weekly.count : data.monthly.count}{' '}
                {t('Widgets.Transactions.transactions', { numberOfTransactions: opt.label })}
              </h4>
            </button>
          );
        })}
      </div>
    </>
  );
};

export default TransactionsCard;
